import type { LoggerService } from '../LoggerService'
import type { LogEntry, SessionSnapshot } from '../logger.types'

const MESSAGE_TYPE = 'container.logger.entry.v2'
const SESSION_MESSAGE_TYPE = 'container.logger.session.v2'
const RATE_WINDOW_MS = 60_000
const MAX_EVENTS_PER_CONTEXT = 300

type BridgeMessage = {
  type: typeof MESSAGE_TYPE
  entry: LogEntry
}

type SessionBridgeMessage = {
  type: typeof SESSION_MESSAGE_TYPE
  snapshot: SessionSnapshot
}

function configuredOrigins(): Set<string> {
  const configured = (process.env.REACT_APP_LOG_ALLOWED_ORIGINS ?? '')
    .split(',')
    .map(origin => origin.trim())
    .filter(Boolean)
  return new Set([window.location.origin, ...configured])
}

function parentOrigin(): string | undefined {
  try {
    if (!document.referrer) return undefined
    return new URL(document.referrer).origin
  } catch {
    return undefined
  }
}

function isBridgeMessage(value: unknown): value is BridgeMessage {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Partial<BridgeMessage>
  return (
    candidate.type === MESSAGE_TYPE &&
    !!candidate.entry &&
    typeof candidate.entry.id === 'string' &&
    typeof candidate.entry.event === 'string' &&
    typeof candidate.entry.sessionId === 'string' &&
    typeof candidate.entry.timestamp === 'number'
  )
}

function isSessionBridgeMessage(value: unknown): value is SessionBridgeMessage {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Partial<SessionBridgeMessage>
  return (
    candidate.type === SESSION_MESSAGE_TYPE &&
    !!candidate.snapshot &&
    typeof candidate.snapshot.sessionId === 'string' &&
    typeof candidate.snapshot.startedAt === 'number'
  )
}

export function mountContextBridge(logger: LoggerService): () => void {
  let isChild = false
  try {
    isChild = window.parent !== window
  } catch {
    isChild = true
  }

  if (isChild) {
    const origin = parentOrigin()
    if (!origin) return () => undefined
    window.parent.postMessage(
      {
        type: SESSION_MESSAGE_TYPE,
        snapshot: logger.getSnapshot()
      } satisfies SessionBridgeMessage,
      origin
    )
    logger.setForwarder(entry => {
      window.parent.postMessage(
        { type: MESSAGE_TYPE, entry } satisfies BridgeMessage,
        origin
      )
      return true
    })
    return () => logger.setForwarder(undefined)
  }

  const allowedOrigins = configuredOrigins()
  const rateState = new Map<string, { startedAt: number; count: number }>()

  const accept = (entry: LogEntry, source: string): void => {
    const key = `${source}:${entry.context?.contextId ?? entry.sessionId}`
    const now = Date.now()
    const current = rateState.get(key)
    const bucket =
      !current || now - current.startedAt >= RATE_WINDOW_MS
        ? { startedAt: now, count: 0 }
        : current
    bucket.count += 1
    rateState.set(key, bucket)
    if (bucket.count > MAX_EVENTS_PER_CONTEXT) {
      logger.noteDropped()
      return
    }
    logger.ingest(entry)
  }

  const onWindowMessage = (event: MessageEvent) => {
    if (!allowedOrigins.has(event.origin)) return
    if (isBridgeMessage(event.data)) accept(event.data.entry, event.origin)
    else if (isSessionBridgeMessage(event.data)) {
      logger.ingestSession(event.data.snapshot)
    }
  }

  const onServiceWorkerMessage = (event: MessageEvent) => {
    if (!isBridgeMessage(event.data)) return
    accept(event.data.entry, 'service-worker')
  }

  window.addEventListener('message', onWindowMessage)
  navigator.serviceWorker?.addEventListener('message', onServiceWorkerMessage)
  return () => {
    window.removeEventListener('message', onWindowMessage)
    navigator.serviceWorker?.removeEventListener(
      'message',
      onServiceWorkerMessage
    )
  }
}
